import React, { useState, useEffect, useRef  } from "react";
import styled from "styled-components"
import { motion, AnimatePresence } from "framer-motion"
import { X } from "lucide-react"
import { useSnackbar } from 'notistack';
import { useForm } from "../hooks/useForm";
import { useProductos } from "../hooks/useProductos";
import AddIcon from '@mui/icons-material/Add';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 0;
  }
`

const ModalContent = styled(motion.div)`
  background-color: #2A2B2E;
  border-radius: 8px;
  width: 100%;
  max-width: 600px;
  height: auto;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    width: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
  }
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #3A3B3E;
`

const ModalTitle = styled.h2`
  color: #fff;
  margin: 0;
  font-size: 1.5rem;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  font-size: 1.5rem;
`

const FormContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #2A2B2E;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #4A4B4F;
    border-radius: 4px;
  }
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Label = styled.label`
  color: #fff;
  font-size: 0.9rem;
`
const Select = styled.select`
padding: 10px;
  border-radius: 4px;
  border: 1px solid #4A4B4F;
  background-color: #3A3B3E;
  color: white;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #4A88F3;
  }
    `

const Textarea = styled.textarea`
padding: 10px;
  border-radius: 4px;
  border: 1px solid #4A4B4F;
  background-color: #3A3B3E;
  color: white;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #4A88F3;
  }
    `
const Input = styled.input`
  padding: 10px;
  border-radius: 4px;
  border: 1px solid #4A4B4F;
  background-color: #3A3B3E;
  color: white;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #4A88F3;
  }
`

const InputPlus = styled.div`
  padding: 10px;
  border-radius: 4px;
  border: 1px solid #4A4B4F;
  background-color: #3A3B3E;
  color: white;
  font-size: 1rem;
  transition: .3s ease;
  cursor: pointer;

  &:hover{
     background-color:rgba(91, 93, 97, 0.6);
  }
  &:focus {
    outline: none;
    border-color: #4A88F3;
  }
`

const ColorInput = styled(Input)`
  height: 40px;
  padding: 5px;
`

const ColorInputPlus = styled(InputPlus)`
  height: 40px;
  padding: 5px;
`

const Button = styled.button`
  padding: 12px 20px;
  border-radius: 4px;
  border: none;
  background-color: #4A88F3;
  color: white;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #3B78E0;
  }

  &:disabled {
    background-color: #4A4B4F;
    cursor: not-allowed;
  }
`


export default function Modal({ isOpen, onClose }) {
  const modalRef = useRef(null)

  function rgbToHex(r, g, b) {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
  }

function convertRgbArrayToHexArray(inputArray) {
  return inputArray.map(colorStr => {
    // Verificar si el valor ya es hexadecimal
    if (/^#[0-9A-F]{6}$/i.test(colorStr)) {
      return colorStr; // Si es hexadecimal, devolver tal cual
    }
    
    // Extraer los valores RGB de la cadena
    const [r, g, b] = colorStr.match(/\d+/g).map(Number);
    // Convertir a hexadecimal
    return rgbToHex(r, g, b);
  });
}

  const { postProducto } = useProductos();
      const { form, onChange } = useForm({
          imagenes: null,
          nombre: "",
          sku: "",
          inventario: "",
          precioAntes: "",
          precio: "",
          categorias: "",
          colores: [],
          fecha: "",
          marco: "",
          tenedor: "",
          llanta_delantera: "",
          llanta_trasera: "",
          cambiador: "",
          descarrilador_delantero: "",
          descarrilador_trasero: "",
          platos_vielas: "",
          cassette: "",
          cadena: "",
          ejes: "",
          frenos: "",
          manubrio: "",
          vastago_manubrio: "",
          tija_sillin: "",
          sillin: "",
          descripcion: "",
          peso: "",
          tipo_marco: "",
          rines: "",
          grupo: "",
          tamano_marco: "",
      });

      const {
          imagenes,
          nombre,
          sku,
          inventario,
          precioAntes,
          precio,
          categorias,
          colores,
          fecha,
          marco,
          tenedor,
          llanta_delantera,
          llanta_trasera,
          cambiador,
          descarrilador_delantero,
          descarrilador_trasero,
          platos_vielas,
          cassette,
          cadena, ejes, frenos, manubrio, vastago_manubrio, tija_sillin, sillin, descripcion, peso, tipo_marco, rines, grupo, tamano_marco } = form;
      const { enqueueSnackbar } = useSnackbar();

      const handlePostProducto = () => {
          if (
              imagenes?.length === 0 || // Verifica si `imagenes` está vacío o no definido
              nombre?.trim() === "" ||
              sku?.trim() === "" ||
              inventario?.trim() === "" ||
              precio?.trim() === "" ||
              categorias?.trim() === "" ||
              colores?.trim() === "" ||
              fecha?.trim() === "" ||
              marco?.trim() === "" ||
              tenedor?.trim() === "" ||
              llanta_delantera?.trim() === "" ||
              llanta_trasera?.trim() === "" ||
              cambiador?.trim() === "" ||
              descarrilador_delantero?.trim() === "" ||
              descarrilador_trasero?.trim() === "" ||
              platos_vielas?.trim() === "" ||
              cassette?.trim() === "" ||
              cadena?.trim() === "" ||
              ejes?.trim() === "" ||
              frenos?.trim() === "" ||
              manubrio?.trim() === "" ||
              vastago_manubrio?.trim() === "" ||
              tija_sillin?.trim() === "" ||
              sillin?.trim() === "" ||
              descripcion?.trim() === "" ||
              peso?.trim() === "" ||
              tipo_marco?.trim() === "" ||
              rines?.trim() === "" ||
              grupo?.trim() === "" ||
              tamano_marco?.trim() === ""
          ) {
              enqueueSnackbar('Todos los campos son obligatorios.', { variant: 'error', anchorOrigin: { vertical: 'bottom', horizontal: 'right' } });
              return; // Detener la ejecución si hay campos vacíos
          }
  
  
          postProducto({
              imagenes,
              nombre,
              sku,
              inventario,
              precio,
              categorias,
              colores,
              fecha,
              marco,
              tenedor,
              llanta_delantera,
              llanta_trasera,
              cambiador,
              descarrilador_delantero,
              descarrilador_trasero,
              platos_vielas,
              cassette,
              cadena, ejes, frenos, manubrio, vastago_manubrio, tija_sillin, sillin, descripcion, peso, tipo_marco, rines, grupo, tamano_marco
          })
      }
    
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener("keydown", handleEscape)
    }

    return () => {
      document.removeEventListener("keydown", handleEscape)
    }
  }, [isOpen, onClose])

  function AddColor(){
    const newColors = [...colores]
    newColors.push("#000000")
    onChange(newColors, 'colores')
  }
  const handleColorChange = (index, event) => {
    if (colores) {
      const newColors = [...colores]
      newColors[index] = event.target.value
      onChange(newColors, 'colores')
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
        >
          <ModalContent
            ref={modalRef}
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{
              type: "spring",
              damping: 20,
              stiffness: 300,
            }}
          >
            <ModalHeader>
              <ModalTitle>Crear Producto</ModalTitle>
              <CloseButton onClick={onClose} aria-label="Cerrar">
                <X size={24} />
              </CloseButton>
            </ModalHeader>
            <FormContainer>
              <Form onSubmit={handlePostProducto}>
                <FormGroup>
                <Label htmlFor="image">Imagenes del producto</Label>
                <Input id="image" type="file" multiple value={imagenes} onChange={(event) => {onChange(event.target.files, "imagenes")}} />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="name">Nombre del producto</Label>
                  <Input id="name" name="name" required />
                </FormGroup>
                <div style={{ display: "flex", gap: 20, justifyContent: "space-between" }}>
                <FormGroup style={{ width: "48%" }}>
                  <Label htmlFor="sku">SKU</Label>
                  <Input id="sku" name="sku"   required />
                </FormGroup>
                <FormGroup style={{ width: "48%" }}>
                  <Label htmlFor="inventory">Inventario</Label>
                  <Input
                    id="inventory"
                    name="inventory"
                    type="text"

                    required
                  />
                </FormGroup>
                </div>

                <div style={{ display: "flex", gap: 20, justifyContent: "space-between" }}>
                <FormGroup style={{ width: "48%" }}>
                  <Label htmlFor="price">Precio</Label>
                  <Input
                    id="price"
                    name="price"
                    type="text"
                    required
                  />
                </FormGroup>
                <FormGroup style={{ width: "48%" }}>
                  <Label htmlFor="disocunt">Precio antes</Label>
                  <Input
                    id="discount"
                    name="discount"
                  />
                </FormGroup>
                </div>

                <FormGroup>
                  <Label htmlFor="category">Categoría</Label>
                  <Input
                    id="category"
                    name="category"
                    required
                  />
                </FormGroup>

                <FormGroup >
                  <Label>Colores</Label>
                    <div style={{ display: "flex", gap: 10 }}>
                  {colores.map((color, index) => (
                      <ColorInput
                      key={index}
                      onChange={(event) => handleColorChange(index, event)}
                      type="color"
                      value={color}
                      />
                    ))}

                    <ColorInputPlus onClick={AddColor}> 
                        <AddIcon></AddIcon>
                    </ColorInputPlus>
                    </div>
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="date">Fecha</Label>
                  <Input
                    id="date"
                    name="date"
                    type="date"
                    required
                  />
                </FormGroup>

                <div style={{ display: "flex", gap: 20, justifyContent: "space-between" }}>
                
                <FormGroup  style={{ width: "48%" }}>
                  <Label htmlFor="marco">Marco</Label>
                  <Input
                    id="marco"
                    name="marco"
                    type="text"
                    required
                  />
                </FormGroup>
                <FormGroup  style={{ width: "48%" }}>
                  <Label htmlFor="marco">Tenedor</Label>
                  <Input
                    id="tenedor"
                    name="tenedor"
                    type="text"
                    required
                  />
                </FormGroup>
                
                </div>

                <div style={{ display: "flex", gap: 20, justifyContent: "space-between" }}>
                
                <FormGroup  style={{ width: "48%" }}>
                  <Label htmlFor="llanta_delantera">Llanta delantera</Label>
                  <Input
                    id="llanta_delantera"
                    name="llanta_delantera"
                    type="text"
                    required
                  />
                </FormGroup>
                <FormGroup  style={{ width: "48%" }}>
                  <Label htmlFor="llanta_trasera">Llanta Trasera</Label>
                  <Input
                    id="llanta_trasera"
                    name="llanta_trasera"
                    type="text"
                    required
                  />
                </FormGroup>
                
                </div>


                
                <div style={{ display: "flex", gap: 20, justifyContent: "space-between" }}>
                
                <FormGroup  style={{ width: "48%" }}>
                  <Label htmlFor="descarrilador_delantero">Descarrilador delantero</Label>
                  <Input
                    id="descarrilador_delantero"
                    name="descarrilador_delantero"
                    type="text"
                    required
                  />
                </FormGroup>
                <FormGroup  style={{ width: "48%" }}>
                  <Label htmlFor="descarrilador_trasero">Descarrilador trasero</Label>
                  <Input
                    id="descarrilador_trasero"
                    name="descarrilador_trasero"
                    type="text"
                    required
                  />
                </FormGroup>
                
                </div>

                  
                <div style={{ display: "flex", gap: 20, justifyContent: "space-between" }}>
                
                <FormGroup  style={{ width: "30%" }}>
                  <Label htmlFor="cambiador">Cambiador</Label>
                  <Input
                    id="cambiador"
                    name="cambiador"
                    type="text"
                    required
                  />
                </FormGroup>
                <FormGroup  style={{ width: "30%" }}>
                  <Label htmlFor="platos_vielas">Platos y vielas</Label>
                  <Input
                    id="platos_vielas"
                    name="platos_vielas"
                    type="text"
                    required
                  />
                </FormGroup>

                <FormGroup  style={{ width: "30%" }}>
                  <Label htmlFor="cassette">Cassette</Label>
                  <Input
                    id="cassette"
                    name="cassette"
                    type="text"
                    required
                  />
                </FormGroup>
                </div>

                <div style={{ display: "flex", gap: 20, justifyContent: "space-between" }}>
                
                <FormGroup  style={{ width: "30%" }}>
                  <Label htmlFor="cadenas">Cadenas</Label>
                  <Input
                    id="cadenas"
                    name="cadenas"
                    type="text"
                    required
                  />
                </FormGroup>
                <FormGroup  style={{ width: "30%" }}>
                  <Label htmlFor="ejes">Ejes</Label>
                  <Input
                    id="ejes"
                    name="ejes"
                    type="text"
                    required
                  />
                </FormGroup>

                <FormGroup  style={{ width: "30%" }}>
                  <Label htmlFor="frenos">Frenos</Label>
                  <Input
                    id="frenos"
                    name="frenos"
                    type="text"
                    required
                  />
                </FormGroup>
                </div>


                
                <div style={{ display: "flex", gap: 20, justifyContent: "space-between" }}>
                
                <FormGroup  style={{ width: "48%" }}>
                  <Label htmlFor="llanta_delantera">Manubrio</Label>
                  <Input
                    id="llanta_delantera"
                    name="llanta_delantera"
                    type="text"
                    required
                  />
                </FormGroup>
                <FormGroup  style={{ width: "48%" }}>
                  <Label htmlFor="vastago_manubrio">Vástago del Manubrio</Label>
                  <Input
                    id="vastago_manubrio"
                    name="vastago_manubrio"
                    type="text"
                    required
                  />
                </FormGroup>
                
                </div>

                <div style={{ display: "flex", gap: 20, justifyContent: "space-between" }}>
                
                <FormGroup  style={{ width: "48%" }}>
                  <Label htmlFor="sillin">Sillin</Label>
                  <Input
                    id="sillin"
                    name="sillin"
                    type="text"
                    required
                  />
                </FormGroup>
                <FormGroup  style={{ width: "48%" }}>
                  <Label htmlFor="tija_silin">Tija del Sillin</Label>
                  <Input
                    id="tija_silin"
                    name="tija_silin"
                    type="text"
                    required
                  />
                </FormGroup>
                
                </div>

                <div style={{ display: "flex", gap: 20, justifyContent: "space-between" }}>
                
                <FormGroup  style={{ width: "30%" }}>
                  <Label htmlFor="peso">Peso</Label>
                  <Input
                    id="peso"
                    name="peso"
                    type="text"
                    required
                  />
                </FormGroup>
                <FormGroup  style={{ width: "30%" }}>
                  <Label htmlFor="rines">Rines</Label>
                  <Input
                    id="rines"
                    name="rines"
                    type="text"
                    required
                  />
                </FormGroup>

                <FormGroup  style={{ width: "30%" }}>
                  <Label htmlFor="frenos">Tipo de Marco</Label>
                  <Select value={tipo_marco} onChange={(event) => onChange(event, "tipo_marco")}>
                        <option value="Sedan">Sedan</option>
                        <option value="Cruiser">Cruiser</option>
                        <option value="Touring">Touring</option>
                  </Select>
                </FormGroup>
                </div>


                <div style={{ display: "flex", gap: 20, justifyContent: "space-between" }}>
                
                <FormGroup  style={{ width: "48%" }}>
                  <Label htmlFor="tamaño_marco">Tamaño de Marco</Label>
                  <Input
                    id="tamaño_marco"
                    name="tamaño_marco"
                    type="text"
                    required
                  />
                </FormGroup>
                <FormGroup  style={{ width: "48%" }}>
                  <Label htmlFor="grupo">Grupo</Label>
                  <Input
                    id="grupo"
                    name="grupo"
                    type="text"
                    required
                  />
                </FormGroup>
                
                </div>

                <FormGroup>
                  <Label htmlFor="description">Descripción</Label>
                  <Textarea
                    id="description"
                    name="description"
                    required
                  />
                </FormGroup>

                <Button type="submit">Guardar Cambios</Button>
              </Form>
            </FormContainer>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  )
}

